import { useEffect, useRef, useState } from 'react';
import axios from 'axios';

// Real file upload (+ in-browser recording) for Media blocks, shared by
// courses/topicBlockEditors.jsx and exercise-templates/block-editors/
// MediaBlockEditor.jsx - the only two Media editors in the app, both were
// previously paste-a-URL-only since no upload endpoint existed. Backed by
// a course-scoped upload endpoint (POST /api/courses/{courseId}/media) - a
// courseId is required, callers without one (e.g. the Exercise Builder
// opened directly from /exercise-templates, no course in context) get the
// disabled/explanatory state below instead of a broken upload attempt.

const ACCEPT_BY_KIND = {
   image: 'image/jpeg,image/png,image/gif,image/webp',
   audio: 'audio/mpeg,audio/wav,audio/ogg',
   video: 'video/mp4,video/webm',
};
const ACCEPT_ANY = [ACCEPT_BY_KIND.image, ACCEPT_BY_KIND.audio, ACCEPT_BY_KIND.video, 'application/pdf'].join(',');
const TYPE_HINT_BY_KIND = { image: 'JPG · PNG · GIF · WEBP', audio: 'MP3 · WAV · OGG', video: 'MP4 · WEBM' };
const TYPE_HINT_ANY = 'Зображення · Аудіо · Відео · PDF';
const KIND_LABELS = { image: 'Зображення', audio: 'Аудіо', video: 'Відео', pdf: 'PDF' };

function kindFromMime(mime) {
   if (!mime) return null;
   if (mime === 'application/pdf') return 'pdf';
   const major = mime.split('/')[0];
   return major === 'image' || major === 'audio' || major === 'video' ? major : null;
}

function formatSeconds(total) {
   const m = Math.floor(total / 60);
   const s = total % 60;
   return m + ':' + String(s).padStart(2, '0');
}

function formatDate(ts) {
   if (!ts) return '';
   const d = new Date(ts);
   if (Number.isNaN(d.getTime())) return '';
   return String(d.getDate()).padStart(2, '0') + '.' + String(d.getMonth() + 1).padStart(2, '0') + '.' + d.getFullYear();
}

// Encodes raw mono PCM samples (Float32, -1..1) into a standard WAV file.
// Needed specifically for audio recording: MediaRecorder's native output
// (audio/webm in Chrome/Edge/Brave, audio/ogg in some Firefox builds)
// isn't reliably one of the backend's allowed audio types (mp3/wav/ogg,
// content-sniffed) across browsers, and there's no dependable way to make
// MediaRecorder itself emit one of those directly - WAV is simple enough
// to build by hand from raw samples that this sidesteps the whole problem,
// working identically regardless of what the browser's audio encoder
// supports.
function encodeWav(samples, sampleRate) {
   const buffer = new ArrayBuffer(44 + samples.length * 2);
   const view = new DataView(buffer);
   const writeString = (offset, str) => {
      for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
   };
   writeString(0, 'RIFF');
   view.setUint32(4, 36 + samples.length * 2, true);
   writeString(8, 'WAVE');
   writeString(12, 'fmt ');
   view.setUint32(16, 16, true);
   view.setUint16(20, 1, true);
   view.setUint16(22, 1, true);
   view.setUint32(24, sampleRate, true);
   view.setUint32(28, sampleRate * 2, true);
   view.setUint16(32, 2, true);
   view.setUint16(34, 16, true);
   writeString(36, 'data');
   view.setUint32(40, samples.length * 2, true);
   let offset = 44;
   for (let i = 0; i < samples.length; i++, offset += 2) {
      const s = Math.max(-1, Math.min(1, samples[i]));
      view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
   }
   return new Blob([view], { type: 'audio/wav' });
}

// Tiny hand-built icon set, same convention already used across the app
// (blockTypeIcons.jsx / OutlineNode.jsx's TopicIcon): 24x24 viewBox, plain
// line/rect/circle/polyline/polygon primitives only (no bezier paths) so
// every glyph stays crisp at small sizes. Kept local rather than imported
// from courses/preview/blockTypeIcons.jsx since that file's icon set is
// keyed by block *type* (multiple_choice, embed_grid, ...) - a different,
// unrelated vocabulary from "what kind of file is this."
function MiniIcon({ children, size }) {
   return (
      <svg viewBox='0 0 24 24' width={size || 14} height={size || 14} fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
         {children}
      </svg>
   );
}
const SearchIcon = () => <MiniIcon size={14}><circle cx='10' cy='10' r='6' /><line x1='15' y1='15' x2='20' y2='20' /></MiniIcon>;
const UploadCloudIcon = () => <MiniIcon size={26}><polyline points='8,11 12,7 16,11' /><line x1='12' y1='7' x2='12' y2='16' /><polyline points='5,16 5,19 19,19 19,16' /></MiniIcon>;
const KIND_ICON_SHAPES = {
   image: <><rect x='3' y='4' width='18' height='16' rx='2' /><circle cx='8' cy='9' r='1.6' /><polyline points='4,17 9,12 13,15 16,11 20,16' /></>,
   audio: <><rect x='4' y='10' width='2' height='4' fill='currentColor' stroke='none' /><rect x='8' y='6' width='2' height='12' fill='currentColor' stroke='none' /><rect x='12' y='3' width='2' height='18' fill='currentColor' stroke='none' /><rect x='16' y='8' width='2' height='8' fill='currentColor' stroke='none' /><rect x='20' y='11' width='2' height='2' fill='currentColor' stroke='none' /></>,
   video: <><rect x='2' y='4' width='20' height='16' rx='2' /><polygon points='10,9 16,12 10,15' fill='currentColor' stroke='none' /></>,
   pdf: <><polygon points='6,2 15,2 19,6 19,22 6,22' /><polyline points='15,2 15,6 19,6' /><line x1='9' y1='11' x2='16' y2='11' /><line x1='9' y1='15' x2='16' y2='15' /></>,
};
const KindIcon = ({ kind }) => <MiniIcon size={14}>{KIND_ICON_SHAPES[kind] || KIND_ICON_SHAPES.pdf}</MiniIcon>;

// One record panel handles both audio (WAV, via AudioContext) and video
// (webm, via plain MediaRecorder) - the two need genuinely different
// capture pipelines (see encodeWav's comment) but share the same
// request-permission -> live -> recording -> stopped/preview state
// machine, so one component with a `kind` branch is clearer than two
// near-duplicate ones.
function RecordPanel({ kind, onSend, onCancel }) {
   const [status, setStatus] = useState('requesting');
   const [seconds, setSeconds] = useState(0);
   const [blob, setBlob] = useState(null);
   const videoRef = useRef(null);
   const streamRef = useRef(null);
   const recorderRef = useRef(null);
   const chunksRef = useRef([]);
   const audioCtxRef = useRef(null);
   const processorRef = useRef(null);
   const sourceRef = useRef(null);
   const samplesRef = useRef([]);
   const timerRef = useRef(null);
   const blobUrlRef = useRef(null);

   const stopTracks = () => {
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
   };

   const requestStream = () => {
      setStatus('requesting');
      const constraints = kind === 'video' ? { audio: true, video: true } : { audio: true };
      navigator.mediaDevices.getUserMedia(constraints).then((stream) => {
         streamRef.current = stream;
         if (kind === 'video' && videoRef.current) {
            videoRef.current.srcObject = stream;
         }
         setStatus('ready');
      }).catch(() => setStatus('denied'));
   };

   useEffect(() => {
      requestStream();
      return () => {
         clearInterval(timerRef.current);
         stopTracks();
         if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   const startRecording = () => {
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
      if (kind === 'video') {
         chunksRef.current = [];
         const recorder = new MediaRecorder(streamRef.current);
         recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
         recorder.onstop = () => setBlob(new Blob(chunksRef.current, { type: 'video/webm' }));
         recorder.start();
         recorderRef.current = recorder;
      } else {
         const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
         const source = audioCtx.createMediaStreamSource(streamRef.current);
         const processor = audioCtx.createScriptProcessor(4096, 1, 1);
         samplesRef.current = [];
         processor.onaudioprocess = (e) => samplesRef.current.push(new Float32Array(e.inputBuffer.getChannelData(0)));
         source.connect(processor);
         processor.connect(audioCtx.destination);
         audioCtxRef.current = audioCtx;
         processorRef.current = processor;
         sourceRef.current = source;
      }
      setStatus('recording');
   };

   const stopRecording = () => {
      clearInterval(timerRef.current);
      if (kind === 'video') {
         recorderRef.current.stop();
      } else {
         processorRef.current.disconnect();
         sourceRef.current.disconnect();
         const sampleRate = audioCtxRef.current.sampleRate;
         audioCtxRef.current.close();
         const total = samplesRef.current.reduce((sum, arr) => sum + arr.length, 0);
         const merged = new Float32Array(total);
         let offset = 0;
         samplesRef.current.forEach((arr) => { merged.set(arr, offset); offset += arr.length; });
         setBlob(encodeWav(merged, sampleRate));
      }
      stopTracks();
      setStatus('stopped');
   };

   const handleRerecord = () => {
      setBlob(null);
      requestStream();
   };

   const handleSend = () => {
      const ext = kind === 'video' ? 'webm' : 'wav';
      onSend(new File([blob], 'recording.' + ext, { type: blob.type }));
   };

   const title = kind === 'video' ? '🎥 Запис відео' : '🎙️ Запис аудіо';
   const header = (
      <div className='media-uploader-record-header'>
         <span className='media-uploader-record-title'>{title}</span>
         <button type='button' className='media-uploader-record-close' onClick={onCancel} title='Скасувати'>✕</button>
      </div>
   );

   if (status === 'denied') {
      return (
         <div className='media-uploader-record-panel'>
            {header}
            <p className='media-uploader-error'>Доступ до {kind === 'video' ? 'камери/мікрофона' : 'мікрофона'} не надано.</p>
         </div>
      );
   }

   if (status === 'stopped' && blob) {
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = URL.createObjectURL(blob);
      return (
         <div className='media-uploader-record-panel'>
            {header}
            {kind === 'video'
               ? <video className='media-uploader-record-preview' src={blobUrlRef.current} controls />
               : <audio className='media-uploader-record-preview' src={blobUrlRef.current} controls />}
            <div className='media-uploader-record-actions'>
               <button type='button' className='exercise-block-save-btn' onClick={handleSend}>Надіслати</button>
               <button type='button' className='exercise-block-cancel-btn' onClick={handleRerecord}>Записати знову</button>
            </div>
         </div>
      );
   }

   return (
      <div className='media-uploader-record-panel'>
         {header}
         {kind === 'video' ? (
            <video className={'media-uploader-record-preview' + (status === 'recording' ? ' media-uploader-record-preview-live' : '')} ref={videoRef} muted autoPlay playsInline />
         ) : (
            <div className={'media-uploader-mic-visual' + (status === 'recording' ? ' media-uploader-mic-visual-live' : '')}>🎙️</div>
         )}
         {status === 'requesting' ? <p className='media-uploader-hint'>Запит доступу...</p> : ''}
         {status === 'recording' ? (
            <div className='media-uploader-record-live'>
               <span className='media-uploader-record-dot' />
               <span className='media-uploader-record-timer'>{formatSeconds(seconds)}</span>
            </div>
         ) : ''}
         <div className='media-uploader-record-actions'>
            {status === 'ready' ? <button type='button' className='exercise-block-save-btn' onClick={startRecording}>Почати запис</button> : ''}
            {status === 'recording' ? <button type='button' className='media-uploader-stop-btn' onClick={stopRecording}>Зупинити</button> : ''}
         </div>
      </div>
   );
}

// Reuse an already-uploaded file instead of uploading a duplicate - "nobody
// will remember a file's path/id to paste it manually," so this browses
// every File this admin tool has ever stored (GET /api/files, unscoped by
// course - reuse across courses/exercises is the whole point) and filters
// client-side, same fetch-all-then-filter shape as
// exercise-templates/ExerciseTemplatePicker.jsx / students/
// ConversationTemplatePicker.jsx (no server-side search param exists on
// this endpoint either). Deliberately available even when `courseId` is
// null - picking an existing file is read-only, it doesn't need the
// course-scoped upload endpoint at all, so this is the one MediaUploader
// capability that still works in the Exercise Builder's "opened directly
// from /exercise-templates" case.
function FileLibraryPicker({ kind, onSelect }) {
   const [files, setFiles] = useState([]);
   const [loading, setLoading] = useState(true);
   const [query, setQuery] = useState('');
   const [open, setOpen] = useState(false);
   const wrapRef = useRef(null);

   useEffect(() => {
      axios.get('/api/files', { withCredentials: true })
         .then((response) => setFiles(Array.isArray(response.data) ? response.data : []))
         .catch(() => setFiles([]))
         .finally(() => setLoading(false));
   }, []);

   useEffect(() => {
      const handleOutside = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
      document.addEventListener('mousedown', handleOutside);
      return () => document.removeEventListener('mousedown', handleOutside);
   }, []);

   // category is "which subsystem uploaded this" (only 'courses' exists
   // today, per File.java's own comment - filtering defensively anyway so
   // a future unrelated feature writing to the same table can't leak in
   // here unnoticed).
   const filtered = files.filter((f) => {
      if (f.category && f.category !== 'courses') return false;
      if (kind && kindFromMime(f.type) !== kind) return false;
      if (!query.trim()) return true;
      return (f.name || '').toLowerCase().includes(query.trim().toLowerCase());
   });

   const handlePick = (file) => {
      onSelect('/api/files/' + file.id + '/content', file);
      setOpen(false);
      setQuery('');
   };

   const emptyMessage = loading ? 'Завантаження списку файлів...'
      : files.length === 0 ? 'Бібліотека порожня - ще немає завантажених файлів.'
      : query.trim() ? 'Нічого не знайдено за запитом.'
      : 'Немає файлів цього типу.';

   return (
      <div className='media-uploader-library' ref={wrapRef}>
         <div className='media-uploader-search-wrap'>
            <span className='media-uploader-search-icon'><SearchIcon /></span>
            <input className='media-uploader-library-input' type='text'
               placeholder='Пошук у бібліотеці файлів...'
               value={query} onFocus={() => setOpen(true)} onChange={(e) => { setQuery(e.target.value); setOpen(true); }} />
         </div>
         {open ? (
            <div className='media-uploader-library-dropdown'>
               {filtered.length === 0 ? (
                  <div className='media-uploader-library-empty'>{emptyMessage}</div>
               ) : filtered.map((f) => {
                  const fk = kindFromMime(f.type);
                  return (
                     <div key={f.id} className='media-uploader-library-option' onClick={() => handlePick(f)}>
                        <span className='media-uploader-library-option-icon'><KindIcon kind={fk} /></span>
                        <span className='media-uploader-library-option-name'>{f.name}</span>
                        <span className='media-uploader-library-option-kind'>{KIND_LABELS[fk] || f.type}</span>
                        <span className='media-uploader-library-option-date'>{formatDate(f.uploadedAt)}</span>
                     </div>
                  );
               })}
            </div>
         ) : ''}
      </div>
   );
}

// `kind`: 'image'|'audio'|'video'|null. null (Topic's single generic
// "media" type has no per-type discrimination the way exercise-templates
// does) means "accept any allowed type, offer both record options."
// Three sources (library / upload / record) used to be stacked flat in one
// long column - restyled as a small segmented tab switcher (same visual
// recipe as .course-workspace-tabs) so only one source's UI is visible at
// a time instead of a wall of same-looking inputs/buttons.
export default function MediaUploader({ courseId, kind, onUploaded }) {
   const fileInputRef = useRef(null);
   const [uploading, setUploading] = useState(false);
   const [progress, setProgress] = useState(0);
   const [error, setError] = useState('');
   const [recordingKind, setRecordingKind] = useState(null);
   const [dragOver, setDragOver] = useState(false);

   const canUpload = !!courseId;
   const canRecordAudio = canUpload && (kind === 'audio' || kind === null);
   const canRecordVideo = canUpload && (kind === 'video' || kind === null);
   const canRecord = canRecordAudio || canRecordVideo;

   const [tab, setTab] = useState(canUpload ? 'upload' : 'library');

   const uploadFile = (file) => {
      setError('');
      setUploading(true);
      setProgress(0);
      setRecordingKind(null);
      const formData = new FormData();
      formData.append('file', file);
      axios.post('/api/courses/' + courseId + '/media', formData, {
         withCredentials: true,
         onUploadProgress: (e) => { if (e.total) setProgress(Math.round((e.loaded / e.total) * 100)); },
      }).then((response) => {
         const fileMeta = response.data;
         const url = '/api/files/' + fileMeta.id + '/content';
         onUploaded(url, fileMeta);
      }).catch((err) => {
         const status = err.response && err.response.status;
         if (status === 415) setError('Непідтримуваний тип файлу. Дозволено: зображення (jpg/png/gif/webp), аудіо (mp3/wav/ogg), відео (mp4/webm), pdf.');
         else if (status === 413) setError('Файл завеликий (максимум 30MB).');
         else setError('Не вдалося завантажити файл. Спробуйте ще раз.');
      }).finally(() => setUploading(false));
   };

   const handleFileInputChange = (e) => {
      const file = e.target.files[0];
      if (file) uploadFile(file);
      e.target.value = '';
   };

   const handleDrop = (e) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files && e.dataTransfer.files[0];
      if (file) uploadFile(file);
   };

   if (recordingKind) {
      return <RecordPanel kind={recordingKind} onSend={uploadFile} onCancel={() => setRecordingKind(null)} />;
   }

   const tabs = [
      { key: 'library', label: 'Бібліотека', icon: '📚' },
      ...(canUpload ? [{ key: 'upload', label: 'Завантажити', icon: '⬆️' }] : []),
      ...(canRecord ? [{ key: 'record', label: 'Записати', icon: '⏺️' }] : []),
   ];
   const typeHint = kind ? TYPE_HINT_BY_KIND[kind] : TYPE_HINT_ANY;

   return (
      <div className='media-uploader'>
         {tabs.length > 1 ? (
            <div className='media-uploader-tabs'>
               {tabs.map((t) => (
                  <button type='button' key={t.key}
                     className={'media-uploader-tab' + (tab === t.key ? ' active' : '')}
                     onClick={() => setTab(t.key)}>
                     <span className='media-uploader-tab-icon'>{t.icon}</span>{t.label}
                  </button>
               ))}
            </div>
         ) : ''}

         {tab === 'library' ? <FileLibraryPicker kind={kind} onSelect={onUploaded} /> : ''}

         {tab === 'upload' && canUpload ? (
            <>
               <div className={'media-uploader-dropzone' + (dragOver ? ' media-uploader-dropzone-active' : '')}
                  onClick={() => !uploading && fileInputRef.current.click()}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}>
                  {uploading ? (
                     <div className='media-uploader-upload-progress'>
                        <div className='media-uploader-progress-track'>
                           <div className='media-uploader-progress-fill' style={{ width: progress + '%' }} />
                        </div>
                        <span className='media-uploader-progress-label'>Завантаження... {progress}%</span>
                     </div>
                  ) : (
                     <>
                        <span className='media-uploader-dropzone-icon'><UploadCloudIcon /></span>
                        <span className='media-uploader-dropzone-title'>Перетягніть файл сюди</span>
                        <span className='media-uploader-dropzone-sub'>або натисніть, щоб обрати з диску</span>
                        <span className='media-uploader-dropzone-types'>{typeHint} · до 30MB</span>
                     </>
                  )}
               </div>
               <input type='file' ref={fileInputRef} accept={kind ? ACCEPT_BY_KIND[kind] : ACCEPT_ANY}
                  style={{ display: 'none' }} onChange={handleFileInputChange} />
            </>
         ) : ''}

         {tab === 'record' && canRecord ? (
            <div className='media-uploader-record-choice'>
               {canRecordAudio ? (
                  <button type='button' className='media-uploader-record-choice-btn' onClick={() => setRecordingKind('audio')} disabled={uploading}>
                     <span className='media-uploader-record-choice-icon'>🎙️</span>
                     <span>Записати аудіо</span>
                  </button>
               ) : ''}
               {canRecordVideo ? (
                  <button type='button' className='media-uploader-record-choice-btn' onClick={() => setRecordingKind('video')} disabled={uploading}>
                     <span className='media-uploader-record-choice-icon'>🎥</span>
                     <span>Записати відео</span>
                  </button>
               ) : ''}
            </div>
         ) : ''}

         {!canUpload ? <p className='media-uploader-hint'>Завантаження нових файлів доступне лише коли ця вправа відкрита з конкретного курсу - але можна обрати вже завантажений файл із бібліотеки вище.</p> : ''}
         {error ? <p className='media-uploader-error'>{error}</p> : ''}
      </div>
   );
}

export { kindFromMime };
